"use client";

import * as React from "react";

import { FetchApiError, fetchStrapi, toStrapiQueryString, type StrapiQuery } from "@/lib/strapi";

export type UseFetchApiState<T> = {
  data: T | null;
  error: FetchApiError | null;
  isLoading: boolean;
  refetch: () => void;
};

type FetchApiCacheEntry = {
  payload: unknown;
  updatedAt: number;
};

const FETCH_API_CACHE_MAX_ENTRIES = 50;
const fetchApiCache = new Map<string, FetchApiCacheEntry>();

function getCachedEntry(key: string): FetchApiCacheEntry | null {
  return fetchApiCache.get(key) ?? null;
}

function getCachedPayload<TResponse>(key: string): TResponse | null {
  const cached = fetchApiCache.get(key);
  return cached ? (cached.payload as TResponse) : null;
}

function buildFetchApiCacheKey(options: {
  path: string;
  queryKey: string;
  withBrandFilter: boolean | undefined;
  brandRelationKey: string | undefined;
}): string {
  const brandFlag = options.withBrandFilter === undefined ? "" : String(options.withBrandFilter);
  const relation = options.brandRelationKey ?? "";
  return `${options.path}?${options.queryKey}::brand=${brandFlag}::rel=${relation}`;
}

function setCachedPayload(key: string, payload: unknown) {
  fetchApiCache.set(key, { payload, updatedAt: Date.now() });
  if (fetchApiCache.size <= FETCH_API_CACHE_MAX_ENTRIES) return;
  let oldestKey: string | null = null;
  let oldestAt = Number.POSITIVE_INFINITY;
  for (const [k, entry] of fetchApiCache.entries()) {
    if (entry.updatedAt < oldestAt) {
      oldestAt = entry.updatedAt;
      oldestKey = k;
    }
  }
  if (oldestKey) fetchApiCache.delete(oldestKey);
}

export function primeFetchApiCache<TResponse>(options: {
  path: string;
  query?: StrapiQuery;
  withBrandFilter?: boolean;
  brandRelationKey?: string;
  payload: TResponse;
}): void {
  const queryKey = toStrapiQueryString(options.query);
  const cacheKey = buildFetchApiCacheKey({
    path: options.path,
    queryKey,
    withBrandFilter: options.withBrandFilter,
    brandRelationKey: options.brandRelationKey,
  });

  if (fetchApiCache.has(cacheKey)) return;
  setCachedPayload(cacheKey, options.payload);
}

export function useFetchApi<TResponse>(
  path: string,
  options?: {
    query?: StrapiQuery;
    enabled?: boolean;
    withBrandFilter?: boolean;
    brandRelationKey?: string;
    init?: Omit<RequestInit, "signal" | "method" | "body">;
    initialData?: TResponse | null;
    revalidateOnMount?: boolean;
    cacheTtlMs?: number;
  },
): UseFetchApiState<TResponse> {
  const {
    query,
    enabled = true,
    withBrandFilter,
    brandRelationKey,
    init,
    initialData,
    revalidateOnMount = initialData ? false : true,
    cacheTtlMs,
  } = options ?? {};

  const queryKey = React.useMemo(() => toStrapiQueryString(query), [query]);
  const cacheKey = React.useMemo(() => {
    return buildFetchApiCacheKey({ path, queryKey, withBrandFilter, brandRelationKey });
  }, [brandRelationKey, path, queryKey, withBrandFilter]);

  const [seedCacheKey] = React.useState(() => (initialData ? cacheKey : null));
  const seededInitialData = seedCacheKey === cacheKey ? initialData : null;

  const [data, setData] = React.useState<TResponse | null>(
    () => getCachedPayload<TResponse>(cacheKey) ?? seededInitialData ?? null,
  );
  const [error, setError] = React.useState<FetchApiError | null>(null);
  const [isLoading, setIsLoading] = React.useState(() => {
    const cached = getCachedPayload<TResponse>(cacheKey);
    if (cached) return false;
    return seededInitialData === undefined || seededInitialData === null;
  });
  const [refetchIndex, setRefetchIndex] = React.useState(0);

  const refetch = React.useCallback(() => {
    setRefetchIndex((prev) => prev + 1);
  }, []);

  React.useEffect(() => {
    if (!enabled) return;

    const cachedEntry = getCachedEntry(cacheKey);
    const cachedPayload = cachedEntry ? (cachedEntry.payload as TResponse) : null;
    if (cachedPayload) {
      setData(cachedPayload);
      setError(null);
      setIsLoading(false);
    }

    const hasSeed = Boolean(cachedPayload ?? seededInitialData);
    const isFresh =
      cacheTtlMs !== undefined && cachedEntry ? Date.now() - cachedEntry.updatedAt < cacheTtlMs : false;

    if (refetchIndex === 0 && isFresh) return;
    if (refetchIndex === 0 && !revalidateOnMount && hasSeed) return;

    const abortController = new AbortController();
    const timeoutId = setTimeout(() => {
      if (abortController.signal.aborted) return;

      const hasCached = Boolean(getCachedEntry(cacheKey));
      if (!hasCached) setIsLoading(true);
      setError(null);

      fetchStrapi<TResponse>(path, {
        queryString: queryKey,
        withBrandFilter,
        brandRelationKey,
        init: { ...(init ?? {}), signal: abortController.signal },
      })
        .then((payload) => {
          if (abortController.signal.aborted) return;
          setCachedPayload(cacheKey, payload);
          setData(payload);
        })
        .catch((err: unknown) => {
          if (abortController.signal.aborted) return;
          const normalized =
            err instanceof FetchApiError ? err : new FetchApiError("Request failed", { cause: err });
          setError(normalized);
          if (!getCachedPayload<TResponse>(cacheKey)) setData(null);
        })
        .finally(() => {
          if (abortController.signal.aborted) return;
          setIsLoading(false);
        });
    }, 0);

    return () => {
      clearTimeout(timeoutId);
      abortController.abort();
    };
  }, [
    cacheKey,
    enabled,
    init,
    seededInitialData,
    path,
    queryKey,
    refetchIndex,
    withBrandFilter,
    brandRelationKey,
    revalidateOnMount,
    cacheTtlMs,
    seedCacheKey,
  ]);

  return { data, error, isLoading, refetch };
}
